import React from 'react'

import { Color, Link, LinkVariant, withCitrusTheme } from '@gousto-internal/citrus-react'

export type CWCLinkButtonProps = {
  href: string
  children: React.ReactNode
  onClick?: () => void
}

/**
 * A navigational CTA that looks like a Citrus primary button but is a real anchor
 * (`<a href>`) under the hood — Citrus has no link-styled-as-button primitive, and
 * elements that navigate to another page should be links, not buttons.
 *
 * Built on the Citrus `Link`, so its typography is inherited; we only override the
 * button appearance (fill, radius, padding, inverted text, hover/focus). Fill/radius use
 * Figma tokens: `Secondary_400` (#615CFF) and an 8px radius. Full-width on mobile, a wider
 * fixed width from tablet up, matching the design. Cast to `ComponentType` because Citrus's
 * `Link` is a forwardRef with a union ref type that `withCitrusTheme`'s overloads don't infer.
 */
const StyledLink = withCitrusTheme(
  Link as React.ComponentType<React.ComponentProps<typeof Link>>,
  (theme) => [
    {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '48px',
      padding: '10px 24px',
      borderRadius: '8px',
      fontFamily: theme.tokens.FontFamilies.bold,
      backgroundColor: theme.color(Color.Secondary_400),
      color: theme.color(Color.White),
      textDecoration: 'none',
      ':hover': {
        backgroundColor: theme.color(Color.Secondary_600),
        color: theme.color(Color.White),
      },
      ':focus-visible': {
        outline: `1px solid ${theme.color(Color.Secondary_600)}`,
        outlineOffset: '2px',
      },
    },
    theme.responsiveStyle(['100%', null, '345px'], ['width']),
  ],
)

export const CWCLinkButton = ({ href, children, onClick }: CWCLinkButtonProps) => (
  <StyledLink href={href} variant={LinkVariant.None} onClick={onClick}>
    {children}
  </StyledLink>
)
